import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { User, AuthResponse, LoginRequest, RegisterRequest } from '../types'
import { authService } from '../services/auth'
import { api } from '../services/api'
import { STORAGE_KEYS } from '../utils/constants'
import { logger } from '../utils/logger'

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  error: string | null
  isAuthenticated: boolean
}

interface AuthActions {
  login: (credentials: LoginRequest) => Promise<void>
  register: (data: RegisterRequest) => Promise<void>
  logout: () => void
  clearError: () => void
  setLoading: (loading: boolean) => void
  refreshUser: () => Promise<void>
  validateStoredToken: () => Promise<void>
}

type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isLoading: false,
      error: null,
      isAuthenticated: false,

      // Actions
      login: async (credentials: LoginRequest) => {
        try {
          logger.store('AuthStore', 'login_start', { email: credentials.email })
          set({ isLoading: true, error: null })
          
          const response: AuthResponse = await authService.login(credentials)
          
          logger.store('AuthStore', 'login_success', { 
            email: credentials.email,
            userId: response.user?.id || 'unknown',
            response: response
          })
          
          // Set token in axios headers immediately for immediate requests
          if (response.access_token) {
            api.defaults.headers.common['Authorization'] = `Bearer ${response.access_token}`
            logger.debug('Token set in axios defaults immediately', {
              component: 'AuthStore',
              action: 'immediate_token_set'
            })
          }
          
          set({
            user: response.user,
            token: response.access_token,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Login failed'
          logger.store('AuthStore', 'login_error', { 
            email: credentials.email,
            error: errorMessage 
          })
          
          set({
            error: errorMessage,
            isLoading: false,
            isAuthenticated: false
          })
          throw error
        }
      },

      register: async (data: RegisterRequest) => {
        try {
          set({ isLoading: true, error: null })
          
          const response: AuthResponse = await authService.register(data)
          
          set({
            user: response.user,
            token: response.access_token,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Registration failed'
          set({
            error: errorMessage,
            isLoading: false,
            isAuthenticated: false
          })
          throw error
        }
      },

      logout: () => {
        logger.store('AuthStore', 'logout_start')
        
        try {
          // Call logout API endpoint
          authService.logout()
        } catch (error) {
          logger.warn('Logout API call failed', {
            component: 'AuthStore',
            error: error instanceof Error ? error.message : 'Unknown error'
          })
        } finally {
          // Clear axios headers immediately
          delete api.defaults.headers.common['Authorization']
          
          // Always clear local state regardless of API call result
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            error: null
          })
          
          logger.store('AuthStore', 'logout_complete')
        }
      },

      clearError: () => {
        set({ error: null })
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      refreshUser: async () => {
        try {
          const token = get().token
          if (!token) {
            throw new Error('No token available')
          }

          set({ isLoading: true })
          const user = await authService.getCurrentUser()
          
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
        } catch (error) {
          console.error('Failed to refresh user:', error)
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false
          })
          throw error
        }
      },

      // Validate stored token on app startup
      validateStoredToken: async () => {
        const state = get()
        
        if (!state.token || !state.isAuthenticated) {
          logger.debug('No stored token to validate', { component: 'AuthStore' })
          return
        }
        
        try {
          logger.debug('Validating stored token', { component: 'AuthStore' })
          
          // Try to get current user to validate token
          const user = await authService.getCurrentUser()
          
          logger.debug('Token validation successful', { 
            component: 'AuthStore',
            userId: user.id 
          })
          
          set({
            user,
            error: null
          })
          
        } catch (error) {
          logger.warn('Stored token validation failed - logging out', {
            component: 'AuthStore',
            error: error instanceof Error ? error.message : 'Unknown error'
          })
          
          // Token is invalid, clear auth state
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            error: 'Session expired. Please login again.'
          })
          
          // Clear axios headers
          delete api.defaults.headers.common['Authorization']
        }
      }
    }),
    {
      name: STORAGE_KEYS.ACCESS_TOKEN,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
)